// 🔹 Stylesheets
import './SignUp.scss';

// 🔹 React Core
import React, { useState } from 'react';

// 🔹 Routing
import { useNavigate, Link } from 'react-router-dom';

// 🔹 Components
import { Button } from '../../components/Button/Button';

// 🔹 Store
import { sessionStore } from 'src/store/sessionStore';
import { uiStore } from 'src/store/uiStore';
import { toast } from 'src/utils/appAlert';
import { isValidEmail } from 'src/utils/validation';

// 🔹 Assets

import AuthBannerImg from 'url:../../../../assets/images/onboarding-trainer/auth-banner.webp';
import { useAuthSignUp } from 'src/api/mutations/useAuthSignUp';
import { useAuthSocialLogin } from 'src/api/mutations/useAuthSocialLogin';
import {
  useGetOnboardingMasterData,
  fetchOnboardingMasterData,
} from 'src/api/mutations/useGetOnboardingMasterData';
import { fetchOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import { queryClient } from 'src/api/queryClient';
import { ReactComponent as InfoIcon } from '../../../../assets/images/onboarding-trainer/info.svg';
import { ReactComponent as EyeClosedIcon } from '../../../../assets/images/onboarding-trainer/eye-closed.svg';
import { ReactComponent as BackIcon } from '../../../../assets/images/onboarding-trainer/back.svg';
import { ReactComponent as EyeOpenIcon } from '../../../../assets/images/onboarding-trainer/eye-open.svg';
import { ReactComponent as GoogleIcon } from '../../../../assets/images/onboarding-trainer/google.svg';
import { ReactComponent as AppleIcon } from '../../../../assets/images/onboarding-trainer/apple.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';

export const SignUp = () => {
  const navigate = useNavigate();
  const setToken = sessionStore((s) => s.setToken);
  const setHasSignedUp = uiStore((s) => s.setHasSignedUp);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const { mutateAsync: signUp, isPending: isSignUpPending } = useAuthSignUp();
  const { mutateAsync: socialLogin, isPending: isSocialLoginPending } = useAuthSocialLogin();

  // 🔹 Preload Master Data while user is typing so the onboarding screens load instantly
  useGetOnboardingMasterData();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSignUpPending || isNavigating) return;

    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Please fill this before continuing';
    else if (!/\p{L}/u.test(name.trim())) newErrors.name = 'Name must contain at least one letter';

    if (!surname.trim()) newErrors.surname = 'Please fill this before continuing';
    else if (!/\p{L}/u.test(surname.trim()))
      newErrors.surname = 'Surname must contain at least one letter';

    if (!email.trim()) newErrors.email = 'Please fill this before continuing';
    else if (!isValidEmail(email)) newErrors.email = 'Please enter a valid email address.';
    if (!password) {
      newErrors.password = 'Please fill this before continuing';
    } else {
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSymbol = /[-!@#$%^&*(),.?":{}|<>_+=/[\\\]~`]/.test(password);

      const pwdErrs = [];
      if (password.length < 8) pwdErrs.push('be at least 8 characters');
      if (!hasUppercase) pwdErrs.push('contain an uppercase letter');
      if (!hasLowercase) pwdErrs.push('contain a lowercase letter');
      if (!hasNumber) pwdErrs.push('contain a number');
      if (!hasSymbol) pwdErrs.push('contain a symbol');

      if (pwdErrs.length > 0) {
        if (pwdErrs.length === 1) {
          newErrors.password = `Password must ${pwdErrs[0]}.`;
        } else if (pwdErrs.length === 2) {
          newErrors.password = `Password must ${pwdErrs[0]} and ${pwdErrs[1]}.`;
        } else {
          const last = pwdErrs.pop();
          newErrors.password = `Password must ${pwdErrs.join(', ')}, and ${last}.`;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsNavigating(true);
      await signUp({ name, surname, email, password });

      setErrors({});
      setHasSignedUp(true);
      // toast.success('Registration successful! Please check your email to verify.', {
      //   position: 'top-right',
      //   style: {
      //     borderRadius: '8px',
      //     background: '#0a0b24',
      //     color: '#fff',
      //     border: '1px solid rgba(255, 255, 255, 0.1)',
      //     minWidth: 'max-content',
      //   },
      //   iconTheme: {
      //     primary: '#ffb703',
      //     secondary: '#000',
      //   },
      // });
      setIsNavigating(false);
      navigate('/onboarding-trainer/signup/verify-email', { state: { email } });
    } catch (error: any) {
      setIsNavigating(false);
      const newErrors: Record<string, string> = {};

      if (error.code === 'auth/email-already-in-use') {
        newErrors.email = 'This email is already registered. Try logging in instead.';
      } else if (error.code === 'auth/invalid-email') {
        newErrors.email = 'Please enter a valid email address.';
      } else if (error.code === 'auth/weak-password') {
        newErrors.password = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/operation-not-allowed') {
        newErrors.email = 'Registration is currently disabled. Please contact support.';
      } else if (error.message) {
        newErrors.email = error.message
          .replace('Firebase: Error ', '')
          .replace(/\(auth.*\)\./, '')
          .trim();
      } else {
        newErrors.email = 'Registration failed. Please try again.';
      }

      setErrors(newErrors);
    }
  };

  const handleSocialLogin = async (providerName: 'google' | 'apple') => {
    try {
      setIsNavigating(true);
      const socialResult = await socialLogin({ providerName });
      const user = socialResult.user;

      // Fetch required data before navigating so it's instantly available
      const [, onboardingStateResult] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['onboarding-master-data'],
          queryFn: fetchOnboardingMasterData,
        }),
        queryClient.fetchQuery({
          queryKey: ['onboarding-state', user.uid],
          queryFn: () => fetchOnboardingState(user.uid),
        }),
      ]);

      const backendProfile = onboardingStateResult?.profile || {};
      let name = backendProfile.name || socialResult.firstName;
      let surname = backendProfile.surname;

      if (
        (!surname || surname === 'Trainer') &&
        socialResult.lastName &&
        socialResult.lastName !== 'Trainer'
      ) {
        surname = socialResult.lastName;
      } else if (!surname) {
        surname = socialResult.lastName || '';
      }

      if (name && name.includes(' ') && (!surname || surname === 'Trainer')) {
        const parts = name.trim().split(/\s+/);
        name = parts[0];
        surname = parts.slice(1).join(' ');
      }

      setToken(await user.getIdToken(), {
        ...backendProfile,
        uid: user.uid,
        name,
        surname,
        avatarUrl:
          backendProfile.avatarUrl ||
          backendProfile.avatar_url ||
          socialResult.avatarUrl ||
          user.photoURL ||
          undefined,
      });

      const lastStep =
        onboardingStateResult?.onboarding?.lastCompletedStep ??
        onboardingStateResult?.profile?.onboarding?.lastCompletedStep ??
        0;

      if (lastStep >= 4) {
        navigate('/onboarding-trainer/dashboard');
      } else {
        navigate('/onboarding-trainer');
      }
    } catch (error: any) {
      setIsNavigating(false);
      toast.error(error.message || 'Social login failed');
    }
  };

  return (
    <div className="onboarding-signup-container">
      {/* Left Side Image (Mobile Header / Desktop Left) */}
      <div className="signup-image-side">
        <img src={AuthBannerImg} alt="Signup Banner" className="signup-image-bg" />
        <div className="signup-image-overlay"></div>
      </div>

      {/* Right Side Form */}
      <div className="signup-form-side">
        <div className="signup-box">
          {isSuccess ? (
            <div
              className="signup-success-message"
              style={{ textAlign: 'center', marginTop: '40px', padding: '0 20px' }}
            >
              <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffb703"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h2
                style={{ color: '#fff', fontSize: '24px', fontWeight: 600, marginBottom: '16px' }}
              >
                Please verify your email
              </h2>
              <p
                style={{
                  color: 'var(--auth-text-muted)',
                  fontSize: '16px',
                  lineHeight: '1.5',
                  maxWidth: '400px',
                  margin: '0 auto 24px auto',
                }}
              >
                Registration successful! Before logging in, please check your inbox and spam folder
                for a verification link.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsSuccess(false);
                  navigate('/onboarding-trainer/login');
                }}
                className="back-to-login-in"
                style={{ margin: '0 auto' }}
              >
                {<BackIcon />} Back to Log in
              </Button>
            </div>
          ) : (
            <>
              {' '}
              <div className="signup-header">
                <div className="logo-placeholder desktop-only">{<LogoIcon />}</div>
                <h1 className="signup-title">
                  Sign up <span className="highlight">for free</span>
                </h1>
                <p className="signup-subtitle">
                  Join miniPT and start building your programs today.
                </p>
              </div>
              <form
                autoComplete="on"
                onSubmit={handleSignUp}
                className="signup-form auth-form-common"
                noValidate
              >
                {/* Dummy fields to trick browser autofill */}
                <input type="email" style={{ display: 'none' }} aria-hidden="true" />
                <input type="password" style={{ display: 'none' }} aria-hidden="true" />
                <div className="input-row">
                  <div className="input-group">
                    <label>NAME</label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="John"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (errors.name) setErrors({ ...errors, name: '' });
                      }}
                      className={errors.name ? 'error-input' : ''}
                    />
                    {errors.name && (
                      <div className="error-message">
                        {<InfoIcon />} <span>{errors.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="input-group">
                    <label>SURNAME</label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Doe"
                      autoComplete="off"
                      value={surname}
                      onChange={(e) => {
                        setSurname(e.target.value);
                        if (errors.surname) setErrors({ ...errors, surname: '' });
                      }}
                      className={errors.surname ? 'error-input' : ''}
                    />
                    {errors.surname && (
                      <div className="error-message">
                        {<InfoIcon />} <span>{errors.surname}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="input-group">
                  <label>EMAIL</label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="john.doe@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors({ ...errors, email: '' });
                    }}
                    className={errors.email ? 'error-input' : ''}
                  />
                  {errors.email && (
                    <div className="error-message">
                      {<InfoIcon />} <span>{errors.email}</span>
                    </div>
                  )}
                </div>

                <div className="input-group">
                  <label>{'PASSWORD'}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="new-password"
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors({ ...errors, password: '' });
                      }}
                      className={errors.password ? 'error-input' : ''}
                    />
                    <button
                      type="button"
                      className="eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="error-message">
                      {<InfoIcon />} <span>{errors.password}</span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="submit-btn"
                  isLoading={isSignUpPending || isNavigating}
                  disabled={isSignUpPending || isSocialLoginPending || isNavigating}
                >
                  {'Sign Up'}
                </Button>

                <p className="privacy-text">
                  {'By continuing you agree with our '}
                  <strong onClick={() => navigate('/privacy-policy')}>
                    {'Privacy Policy'}
                  </strong>{' '}
                  and{' '}
                  <strong onClick={() => navigate('/terms-of-service')}>
                    {'Terms & Conditions'}
                  </strong>
                </p>
              </form>
              <div className="auth-divider">
                <span>{'OR SIGN UP WITH'}</span>
              </div>
              <div className="auth-social-login">
                <button
                  type="button"
                  className="social-btn google-btn"
                  onClick={() => handleSocialLogin('google')}
                  disabled={isSignUpPending || isSocialLoginPending || isNavigating}
                >
                  {<GoogleIcon />}
                  Google
                </button>
                <button
                  type="button"
                  className="social-btn apple-btn"
                  onClick={() => handleSocialLogin('apple')}
                  disabled={isSignUpPending || isSocialLoginPending || isNavigating}
                >
                  {<AppleIcon />}
                  Apple
                </button>
              </div>
              <p className="auth-bottom-link">
                Already have an account? <Link to="/onboarding-trainer/login">Log in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
